import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Informations from "./Profil/Informations";
import Contrats from "./Profil/Contrats";
import Bulletins from "./Profil/Bulletins";
import Documents from "./Profil/Documents";
import { apiService } from "../../components/Config/api.service";
import EmployeeInfo from "./EmployeeInfo";
import Parametre from "./Profil/Parametre";
import { FaArrowLeft, FaArrowRight, FaArchive, FaTrashRestore } from "react-icons/fa";
import FinalSettlement from "./Profil/FinalSettlement";
import LeaveDeparture from "./Profil/LeaveDeparture";
import Drawer from "../../components/Tools/DrawerCustom";
import ContractForm from "./Contract/ContractEmpForm";
import ExternalContractForm from "./Contract/ExternalContractForm";
import EmployeeForm from "./EmployeeForm"; 
import ActionDropdown from "../../components/Utils/ActionDropdown";
import { notify } from "../../components/Utils/Notifications"; 

function Employee() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [employee, setEmployee] = useState({
    name: "",
    email: "",
    poste: "",
    manager: "",
    phone: "",
    address: "",
    maritalStatus: "",
    spouseIncome: false,
    numberOfChildren: 0,
    nationality: "",
    birthday: "",
    image: false,
    active:true,
  });
  const [contracts, setContracts] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [payslipData, setPayslipData] = useState();
  const [primes, setPrimes] = useState([]);
  const [currentContract, setCurrentContract] = useState(null);
  const [externalContract, setExternalContract] = useState([]);
  const [finalSettlementRecap, setFinalSettlementRecap] = useState(null); 
  const [leaveDepartureRecap, setLeaveDepartureRecap] = useState(null);
  const [activeTab, setActiveTab] = useState("Informations");
  const [isDrawerOpenContracts, setIsDrawerOpenContracts] = useState(false);
  const [isDrawerOpenExternalContracts, setIsDrawerOpenExternalContracts] = useState(false);
  const [isDrawerOpenInformations, setIsDrawerOpenInformations] = useState(false);
  const [prevId, setPrevId] = useState(null);
  const TAB_MAPPING = {
    informations: 'Informations',
    contrat: 'Contrats',
    contrats: 'Contrats',
    contract: 'Contrats',
    documents: 'Documents',
    parametres: 'Parametres',
    parameters: 'Parametres',
    finalsettlement: 'FinalSettlement',
    final_settlement: 'FinalSettlement',
    leavedeparture: 'LeaveDeparture',
    leave_departure: 'LeaveDeparture',
    bulletins: 'Bulletins',
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab')?.toLowerCase();
    const mappedTab = tab ? TAB_MAPPING[tab] : null;
    if (mappedTab && mappedTab !== activeTab) {
      setActiveTab(mappedTab);
    }
  }, [location.search, activeTab]);

  const [nextId, setNextId] = useState(null);
  const [prevName, setPrevName] = useState("");
  const [nextName, setNextName] = useState("");

  const handleBack = useCallback(() => {
    if (location.search) navigate(`/employees${location.search}`);
    else navigate('/employees');
  }, [location, navigate]);

  const refreshEmployeeinfo = useCallback(() => {
    apiService.getElementById("/employee", id)
      .then((response) => setEmployee(response.data))
      .catch((error) => console.error("Error fetching employee:", error));
  }, [id]);

  const refreshContractInfo = useCallback(() => {
    const url = employee.active 
      ? `/employee/contracts/${id}` 
      : `/employee/contracts/${id}?includeArchived=true`;
    apiService.getElement(url)
      .then((response) => {
        const openContract = response.data.contracts.find(contract => contract.Statut === "open") || null;
        setCurrentContract(openContract);
        setContracts(response.data.contracts);
        if (openContract == null) {
            apiService.getElementById("/external_contract/employee", id)
            .then((response) => {
              setExternalContract(response.data.external_contract);
            }).catch(() => setExternalContract(null))
        } else {
            // clear externalContract when a regular contract is present
            setExternalContract(null);
        }
      })
      .catch((error) => console.error("Error fetching contracts:", error));
  }, [id, employee.active]);

  const refreshFinalSettlementRecap = useCallback(() => {
    if (id && currentContract?.id) {
      apiService.getElementById("/final_settlement/recap", currentContract.id)
        .then(res => setFinalSettlementRecap(res.data.final_settlement))
        .catch(() => setFinalSettlementRecap(null));
    }
  }, [id, currentContract]);

  const refreshLeaveDepartureRecap = useCallback(() => {
    if (id && currentContract?.id) {
      apiService.getElementById("/leave_departure/recap", currentContract.id)
        .then(res => setLeaveDepartureRecap(res.data.leaves_departure))
        .catch(() => setLeaveDepartureRecap(null));
    }
  }, [id, currentContract]);

  

  const refreshPrimesInfo = useCallback(() => {
    apiService.getElementById("/employee/primes", id)
      .then((response) => setPrimes(response.data.primes))
      .catch((error) => console.error("Error fetching primes:", error));
  }, [id]);

  const refreshPayslipInfo = useCallback(() => {
    apiService.getElementById("/employee/payslips", id)
      .then((response) => {
        const fetchedPayslips = response.data.payslips;
        setPayslips(fetchedPayslips);
        const currentPayslip = fetchedPayslips.find((p) =>
          p.De.startsWith(new Date().toISOString().slice(0, 7))
        );
        setPayslipData(currentPayslip);
      })
      .catch((error) => console.error("Error fetching payslips:", error));
  }, [id]);


  // Rafraîchir les infos liées au contrat courant
  useEffect(() => {
    if (currentContract?.id) {
      refreshFinalSettlementRecap();
      refreshLeaveDepartureRecap();
    }
  }, [currentContract, refreshFinalSettlementRecap, refreshLeaveDepartureRecap]);

  // Rafraîchir toutes les infos quand l'id change
  useEffect(() => {
    if (id) {
      refreshEmployeeinfo();
      refreshContractInfo();
      refreshPrimesInfo();
      refreshPayslipInfo();
    }
  }, [id, refreshEmployeeinfo, refreshContractInfo, refreshPrimesInfo, refreshPayslipInfo]);

  // Récupère les IDs précédent et suivant à chaque changement d'id
  useEffect(() => {
    if (id) {
      apiService.getElement(`/employee/navigation/${id}`)
        .then(res => {
          setPrevId(res.data.prev_id);
          setNextId(res.data.next_id);
          setPrevName(res.data.prev_name || "");
          setNextName(res.data.next_name || "");
        })
        .catch((error) => console.error("Error fetching employee navigation:", error));
    }
  }, [id]);

  const handlePrev = () => {
    if (prevId) navigate(`/employees/${prevId}`);
  };
  const handleNext = () => {
    if (nextId) navigate(`/employees/${nextId}`);
  };

  const handleArchive = useCallback(async (archiveStatus, options = {}) => {
      try {
          const response = await apiService.saveElement('/employees/archive', {
              // tranforme l'id en integer
              employee_ids: [ parseInt(id)],
              archive: archiveStatus,
              confirm_payslip_cleanup: !!options.confirmPayslipCleanup,
          });
          const archiveResponse = response.data?.result || response.data;
          if (archiveResponse?.needs_confirmation) {
            return archiveResponse;
          }
          notify(archiveStatus ? t('employeeDetails.archiveSuccess') : t('employeeDetails.unarchiveSuccess'), "", 2);
          refreshEmployeeinfo();
          refreshContractInfo();
          refreshPayslipInfo();
          return archiveResponse;
      } catch (e) {
          notify(archiveStatus ? t('employeeDetails.archiveError') : t('employeeDetails.unarchiveError'), "", 3);
          console.error('Erreur archivage employé', e);
          throw e;
      }
  }, [id, refreshEmployeeinfo, refreshContractInfo, refreshPayslipInfo]);

  const handleFormSuccessEmployee = useCallback(() => {
    setIsDrawerOpenInformations(false);
    refreshEmployeeinfo();
  }, [refreshEmployeeinfo]);

  const handleFormSuccessContract = useCallback(() => {
    setIsDrawerOpenContracts(false);
    refreshContractInfo();
    refreshPayslipInfo();
    refreshPrimesInfo();
  }, [refreshContractInfo, refreshPayslipInfo, refreshPrimesInfo]);

  const handleFormSuccessExternalContract = useCallback(() => {
    setIsDrawerOpenExternalContracts(false);
    // refresh contracts/external contract after create/update
    refreshContractInfo();
    refreshPayslipInfo();
    refreshPrimesInfo();
  }, [refreshContractInfo, refreshPayslipInfo, refreshPrimesInfo]);

  const handleFinalSettlement = useCallback(() => {
    refreshFinalSettlementRecap();
    setActiveTab("Contrats");
  }, [refreshFinalSettlementRecap]);

  const handleLeaveDeparture = useCallback(() => {
    refreshLeaveDepartureRecap(); 
    refreshContractInfo();
    setActiveTab("Contrats");
  }, [refreshLeaveDepartureRecap, refreshContractInfo]);

  const handleDeleteFinalSettlement = useCallback(async () => {
    if (!finalSettlementRecap?.id) return;
    try {
      await apiService.updateElementById("/final_settlement/delete", finalSettlementRecap.id, []);
      handleFinalSettlement();
    } catch (err) {
      console.error("Erreur lors de la suppression du final settlement", err);
      if (err.response?.status === 403) {
        notify(t('employeeDetails.insufficientRights'), "top-right", 3);
      } else {
        notify(err.response?.data?.error || t('employeeDetails.serverError'), "top-right", 3);
      }
    }
  }, [finalSettlementRecap, handleFinalSettlement, t]);

  const handleDeleteLeaveDeparture = useCallback(async () => {
    if (!leaveDepartureRecap?.id) return;
    try {
      await apiService.updateElementById("/leave_departure/delete", leaveDepartureRecap.id, []);
      refreshLeaveDepartureRecap();
    } catch (err) {
      console.error("Erreur lors de la suppression du départ en congé", err);
      if (err.response?.status === 403) {
        notify(t('employeeDetails.insufficientRights'), "top-right", 3);
      } else {
        notify(err.response?.data?.error || t('employeeDetails.serverError'), "top-right", 3);
      }
    }
  }, [leaveDepartureRecap, refreshLeaveDepartureRecap, t]);

  const renderContent = useMemo(() => {
    switch (activeTab) {
      case "Informations":
        return <Informations 
                setIsDrawerOpen={setIsDrawerOpenInformations} 
                employee={employee} 
                payslipData={payslipData} 
                currentContract={currentContract}
              />;
      case "Contrats":
        return <Contrats 
                finalSettlementRecap={finalSettlementRecap} 
                leaveDepartureRecap={leaveDepartureRecap}
                setIsDrawerOpenInformations={setIsDrawerOpenInformations} 
                setIsDrawerOpenContracts={setIsDrawerOpenContracts} 
                setIsDrawerOpenExternalContracts={setIsDrawerOpenExternalContracts}
                primes={primes} 
                contracts={contracts} 
                setActiveTab={setActiveTab} 
                employee={employee} 
                currentContract={currentContract} 
                externalContract={externalContract} 
                id={id} 
                refreshContractInfo={refreshContractInfo}    // <--- added
                handleDeleteFinalSettlement={handleDeleteFinalSettlement}    
                handleDeleteLeaveDeparture={handleDeleteLeaveDeparture}            
              />;
      case "Bulletins":
        return <Bulletins payslips={payslips} />;
      // à décommenter quand la partie documents sera prête côté backend
      // case "Documents":
      //   return <Documents />;
      case "Parametres":
        return <Parametre />;
      case "FinalSettlement":
        return <FinalSettlement 
                setActiveTab={setActiveTab} 
                employee={employee} 
                currentContract={currentContract}
                finalSettlementId={finalSettlementRecap?.id} 
                refreshRecap={handleFinalSettlement}
                handleDeleteFinalSettlement={handleDeleteFinalSettlement}
              />;
      case "LeaveDeparture":
        return (
          <LeaveDeparture
            setActiveTab={setActiveTab}
            employee={employee}
            currentContract={currentContract}
            leaveDepartureId={leaveDepartureRecap?.id}
            refreshRecap={handleLeaveDeparture}
            handleDeleteLeaveDeparture={handleDeleteLeaveDeparture}
          />
        );
      default:
        return <Informations employee={employee} />;
    }
  }, [
    activeTab, employee, payslipData, currentContract, finalSettlementRecap, leaveDepartureRecap,
    primes, contracts, id, handleDeleteFinalSettlement, handleDeleteLeaveDeparture,
    handleFinalSettlement, handleLeaveDeparture, payslips, externalContract
  ]);

  // const dropdownActions = useMemo(() => [
  //   {
  //       label: employee.active ? "Archiver" : "Désarchiver",
  //       icon: employee.active ? <FaArchive /> : <FaTrashRestore />,
  //       onClick: () => handleArchive(employee.active),
  //       color: employee.active ? 'text-red-500' : 'text-green-500'
  //   }
  // ], [employee.active, handleArchive]);

  return (
    <>
      <div className="flex items-center gap-4 justify-between mb-4">
        <div className="flex justify-between  items-center gap-2">
          <button onClick={handleBack} className="flex items-center gap-4 text-[--color-primary]">
            <FaArrowLeft className="text-[--color-primary]" />
            {t('employeeDetails.backToList')}
          </button>
        </div>
        {employee.active ?(
        <div className="flex items-center gap-2">
          <button
              onClick={handlePrev}
              disabled={!prevId}
              className={`ml-2 px-2 py-1 rounded-full flex items-center gap-2 ${!prevId ? 'bg-gray-200 text-gray-400' : 'bg-[--color-primary] text-white'}`}
              title={prevName ? t('employeeDetails.previousWithName', { name: prevName }) : t('employeeDetails.previous')}
            >
              <FaArrowLeft />
              {prevName && <span className="hidden md:inline">{prevName}</span>}
            </button>
            <button
              onClick={handleNext}
              disabled={!nextId}
              className={`ml-2 px-2 py-1 rounded-full flex items-center gap-2 ${!nextId ? 'bg-gray-200 text-gray-400' : 'bg-[--color-primary] text-white'}`}
              title={nextName ? t('employeeDetails.nextWithName', { name: nextName }) : t('employeeDetails.next')}
            >
              {nextName && <span className="hidden md:inline">{nextName}</span>}
              <FaArrowRight />
            </button>
        </div>
        ) : (
        <div className="flex items-center gap-2">
                <span className="badge badge-warning gap-2">
                    <FaArchive />
                    {t('employeeDetails.archived')}
                </span>
            {/* <ActionDropdown actions={dropdownActions} /> */}
        </div>
        )}
      </div>
      <div className="flex md:flex-row rounded-lg gap-4 w-full">
        <div className="rounded-lg bg-gray-50 w-72">
          <EmployeeInfo
            employee={employee}
            currentContract={currentContract}
            setActiveTab={setActiveTab}
            handleArchive={handleArchive}
          />
        </div>

        <div className=" rounded-lg overflow-hidden col-span-4  bg-white flex-1">
          {activeTab !== "FinalSettlement" && (
            <div className="p-3 bg-white flex justify-start gap-10 md:flex-wrap mb-3">
              <button
                className={`pb-2 ${activeTab === "Informations" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Informations")}
              >
                {t('employeeDetails.tabGeneral')}
              </button>
              <button
                className={`pb-2 ${activeTab === "Contrats" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Contrats")}
              >
                {t('employeeDetails.tabContract')}
              </button>
              {/* <button
                className={`pb-2 ${activeTab === "Documents" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Documents")}
              >
                Documents
              </button> */}
              {/* <button
                className={`pb-2 ${activeTab === "Parametres" ? "text-[--color-primary] font-bold border-b-2 border-[--color-primary]" : "text-gray-400"}`}
                onClick={() => setActiveTab("Parametres")}
              >
                Parametre du compte
              </button> */}
            </div>
          )}
          {/* Drawer pour le formulaire de contrat */}
          <Drawer isOpen={isDrawerOpenContracts} setIsOpen={setIsDrawerOpenContracts}>
            <ContractForm
              key={employee?.id || 'new'}
              currentContract={currentContract}
              employee={employee}
              refreshContract={refreshContractInfo}
              onSuccess={handleFormSuccessContract}
            />
          </Drawer>
          {/* Drawer pour le formulaire de contrat externe */}
          <Drawer isOpen={isDrawerOpenExternalContracts} setIsOpen={setIsDrawerOpenExternalContracts}>
            <ExternalContractForm
              key={employee?.id || 'new'}
              employee={employee}
              currentExternal={externalContract}
              onSuccess={handleFormSuccessExternalContract}
              onCancel={() => setIsDrawerOpenExternalContracts(false)}
            />
          </Drawer>
          {/* Drawer pour modifier les informations */}
          <Drawer isOpen={isDrawerOpenInformations} setIsOpen={setIsDrawerOpenInformations}>
            <EmployeeForm
              key={employee?.id || 'new'}
              refreshEmployee={refreshEmployeeinfo}
              onSuccess={handleFormSuccessEmployee}
            />
          </Drawer>
          {renderContent}
        </div>
      </div>
    </>
  );
}

export default Employee;
